import { Component } from '@theme/component';

/**
 * @typedef {Object} GiftCardRecipientRefs
 * @property {HTMLInputElement} toggle
 * @property {HTMLElement} fields
 * @property {HTMLInputElement} sendToRecipient
 * @property {HTMLInputElement} offset
 * @property {HTMLInputElement} recipientEmail
 * @property {HTMLInputElement} recipientName
 * @property {HTMLTextAreaElement} message
 * @property {HTMLElement} charCount
 * @property {HTMLInputElement} sendOn
 * @property {HTMLElement} error
 *
 * @extends {Component<GiftCardRecipientRefs>}
 */
class GiftCardRecipient extends Component {
  requiredRefs = ['toggle', 'fields', 'sendToRecipient', 'offset', 'recipientEmail'];

  /** @type {HTMLInputElement[]} */
  #fieldInputs = [];

  connectedCallback() {
    super.connectedCallback();

    this.#fieldInputs = /** @type {HTMLInputElement[]} */ (
      [...this.refs.fields.querySelectorAll('input:not([type="hidden"]), textarea')]
    );

    this.refs.toggle.addEventListener('change', this.#onToggle);
    this.refs.message?.addEventListener('input', this.#onMessageInput);
    this.#setSendOnConstraints();
  }

  #onToggle = () => {
    const checked = this.refs.toggle.checked;
    this.refs.fields.hidden = !checked;
    this.refs.sendToRecipient.value = checked ? 'true' : '';
    this.refs.offset.value = String(new Date().getTimezoneOffset());

    for (const input of this.#fieldInputs) {
      input.disabled = !checked;
    }

    this.refs.offset.disabled = !checked;

    if (!checked) {
      this.#clearFields();
    }
  };

  /** @param {Event} event */
  #onMessageInput = (event) => {
    const textarea = /** @type {HTMLTextAreaElement} */ (event.target);
    if (this.refs.charCount) {
      this.refs.charCount.textContent = String(textarea.value.length);
    }
  };

  #setSendOnConstraints() {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 90);

    const format = (/** @type {Date} */ d) => d.toISOString().split('T')[0];

    this.refs.sendOn?.setAttribute('min', format(today));
    this.refs.sendOn?.setAttribute('max', format(maxDate));
  }

  #clearFields() {
    for (const input of this.#fieldInputs) {
      if (input instanceof HTMLTextAreaElement) {
        input.value = '';
      } else {
        input.value = '';
      }
    }

    if (this.refs.charCount) {
      this.refs.charCount.textContent = '0';
    }

    this.refs.error.hidden = true;
    this.refs.error.textContent = '';
  }

  /** @param {Event} event */
  clearError = (event) => {
    this.refs.error.hidden = true;
    this.refs.error.textContent = '';
  };
}

if (!customElements.get('gift-card-recipient')) {
  customElements.define('gift-card-recipient', GiftCardRecipient);
}
